/**
 * Roadmap Store
 *
 * Manages Roadmap documents and their nodes, edges, swimlanes, and
 * milestone columns. A Roadmap is a forward-chronological product
 * planning artifact: you start from now and plan toward a desired
 * future state, mapping conditions and milestones at each step.
 *
 * Edge creation goes through the create_roadmap_edge RPC to prevent
 * dependency cycles. If the RPC returns error.code === 'cycle_detected',
 * the optimistic edge is rolled back.
 *
 * Per Build Contract: Supabase is source of truth. Zustand is a cache.
 */

import { create } from 'zustand';
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  generateUUID,
} from '../../lib/supabaseSync';

// ── Types ─────────────────────────────────────────────────────

export type RoadmapViewMode = 'Tree' | 'Grid' | 'Split' | 'Canvas';

export type RoadmapNodeTense = 'past' | 'present' | 'future';

export type RoadmapNodeStatus = 'unknown' | 'true' | 'in-progress' | 'not-true';

export interface RoadmapDocument {
  id: string;
  workspaceId: string;
  title: string;
  objective: string;
  futurePoint: string;
  definedSuccess: string | null;
  viewMode: RoadmapViewMode;
  /** Optional: the Plan of Record (cycle) this roadmap informs. */
  cycleId: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export type RoadmapCellRelationship = 'sequential' | 'parallel' | 'then' | 'divider';

export interface RoadmapNode {
  id: string;
  roadmapId: string;
  workspaceId: string;
  text: string;
  tense: RoadmapNodeTense;
  isGoal: boolean;
  laneId: string | null;
  milestoneId: string | null;
  position: number;
  status: RoadmapNodeStatus;
  /** Relationship to the node immediately above in the same cell (ignored for the topmost node). */
  cellRelationship: RoadmapCellRelationship;
  /** Cosmetic label for `divider` connectors only; ignored by planning semantics. */
  cellConnectorCaption: string | null;
  tags: string[];
  /** Linked prioritization workspace item; null means planning-only until saved as item. */
  itemId: string | null;
  /** Custom field values keyed by attribute.key (stored in roadmap_nodes.metadata). */
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface RoadmapEdge {
  id: string;
  roadmapId: string;
  workspaceId: string;
  fromNodeId: string;
  toNodeId: string;
  note: string | null;
  createdAt: string;
}

export interface RoadmapSwimlane {
  id: string;
  roadmapId: string;
  workspaceId: string;
  name: string;
  description: string | null;
  position: number;
  createdAt: string;
}

export interface RoadmapMilestoneColumn {
  id: string;
  roadmapId: string;
  workspaceId: string;
  name: string;
  position: number;
  description: string | null;
  condition: string | null;
  createdAt: string;
}

export type CreateEdgeError = 'cycle_detected' | 'self_loop' | 'duplicate' | 'network';

// ── Store interface ───────────────────────────────────────────

interface RoadmapState {
  documents: RoadmapDocument[];
  nodes: RoadmapNode[];
  edges: RoadmapEdge[];
  swimlanes: RoadmapSwimlane[];
  milestones: RoadmapMilestoneColumn[];
  isLoading: boolean;
  loadedRoadmapId: string | null;
  error: string | null;

  // Document CRUD
  fetchDocuments: () => Promise<void>;
  fetchRoadmap: (roadmapId: string) => Promise<void>;
  createRoadmap: (input: { title: string; objective?: string; futurePoint?: string }) => Promise<{ roadmap: RoadmapDocument | null; error?: string }>;
  updateRoadmap: (
    id: string,
    updates: Partial<
      Pick<RoadmapDocument, 'title' | 'objective' | 'futurePoint' | 'definedSuccess' | 'viewMode' | 'cycleId'>
    >,
  ) => Promise<{ success: boolean; error?: string }>;
  deleteRoadmap: (id: string) => Promise<{ success: boolean; error?: string }>;

  // Node CRUD
  createNode: (roadmapId: string, input: Partial<RoadmapNode>) => Promise<{ node: RoadmapNode | null; error?: string }>;
  updateNode: (id: string, updates: Partial<RoadmapNode>) => Promise<{ success: boolean; error?: string }>;
  deleteNode: (id: string) => Promise<{ success: boolean; error?: string }>;

  // Edge CRUD (create goes through RPC for cycle detection)
  createEdge: (roadmapId: string, fromNodeId: string, toNodeId: string, note?: string) => Promise<{ edge: RoadmapEdge | null; error?: string; errorCode?: CreateEdgeError }>;
  deleteEdge: (id: string) => Promise<{ success: boolean; error?: string }>;

  // Swimlanes
  createSwimlane: (roadmapId: string, name: string) => Promise<{ swimlane: RoadmapSwimlane | null; error?: string }>;
  updateSwimlane: (id: string, updates: { name?: string; position?: number; description?: string | null }) => Promise<{ success: boolean; error?: string }>;
  deleteSwimlane: (id: string) => Promise<{ success: boolean; error?: string }>;

  // Milestones
  createMilestone: (roadmapId: string, name: string) => Promise<{ milestone: RoadmapMilestoneColumn | null; error?: string }>;
  updateMilestone: (id: string, updates: Partial<RoadmapMilestoneColumn>) => Promise<{ success: boolean; error?: string }>;
  deleteMilestone: (id: string) => Promise<{ success: boolean; error?: string }>;

  // Selectors
  getDocumentById: (id: string) => RoadmapDocument | undefined;
  getNodesForRoadmap: (roadmapId: string) => RoadmapNode[];
  getEdgesForRoadmap: (roadmapId: string) => RoadmapEdge[];
  getSwimlanesForRoadmap: (roadmapId: string) => RoadmapSwimlane[];
  getMilestonesForRoadmap: (roadmapId: string) => RoadmapMilestoneColumn[];
}

// ── Helpers ───────────────────────────────────────────────────

function normalizeRoadmapCellRelationship(raw: unknown): RoadmapCellRelationship {
  const v = typeof raw === 'string' ? raw : '';
  if (v === 'sequential' || v === 'parallel' || v === 'then' || v === 'divider') return v;
  return 'parallel';
}

function rowToDoc(r: Record<string, unknown>): RoadmapDocument {
  return {
    id: r.id as string,
    workspaceId: r.workspace_id as string,
    title: (r.title as string) ?? 'Untitled Roadmap',
    objective: (r.objective as string) ?? '',
    futurePoint: (r.future_point as string) ?? '',
    definedSuccess: (r.defined_success as string) ?? null,
    viewMode: ((r.view_mode as string) ?? 'Tree') as RoadmapViewMode,
    cycleId: (r.cycle_id as string) ?? null,
    createdBy: (r.created_by as string) ?? null,
    createdAt: r.created_at as string,
    updatedAt: r.updated_at as string,
  };
}

function rowToNode(r: Record<string, unknown>): RoadmapNode {
  return {
    id: r.id as string,
    roadmapId: r.roadmap_id as string,
    workspaceId: r.workspace_id as string,
    text: (r.text as string) ?? '',
    tense: ((r.tense as string) ?? 'future') as RoadmapNodeTense,
    isGoal: (r.is_goal as boolean) ?? false,
    laneId: (r.lane_id as string) ?? null,
    milestoneId: (r.milestone_id as string) ?? null,
    position: (r.position as number) ?? 0,
    status: ((r.status as string) ?? 'unknown') as RoadmapNodeStatus,
    cellRelationship: normalizeRoadmapCellRelationship(r.cell_relationship),
    cellConnectorCaption: (r.cell_connector_caption as string) ?? null,
    tags: (r.tags as string[]) ?? [],
    itemId: (r.item_id as string) ?? null,
    metadata: (r.metadata as Record<string, unknown>) ?? {},
    createdAt: r.created_at as string,
    updatedAt: r.updated_at as string,
  };
}

function rowToEdge(r: Record<string, unknown>): RoadmapEdge {
  return {
    id: r.id as string,
    roadmapId: r.roadmap_id as string,
    workspaceId: r.workspace_id as string,
    fromNodeId: r.from_node_id as string,
    toNodeId: r.to_node_id as string,
    note: (r.note as string) ?? null,
    createdAt: r.created_at as string,
  };
}

function rowToSwimlane(r: Record<string, unknown>): RoadmapSwimlane {
  return {
    id: r.id as string,
    roadmapId: r.roadmap_id as string,
    workspaceId: r.workspace_id as string,
    name: (r.name as string) ?? 'Lane',
    description: (r.description as string) ?? null,
    position: (r.position as number) ?? 0,
    createdAt: r.created_at as string,
  };
}

function rowToMilestone(r: Record<string, unknown>): RoadmapMilestoneColumn {
  return {
    id: r.id as string,
    roadmapId: r.roadmap_id as string,
    workspaceId: r.workspace_id as string,
    name: (r.name as string) ?? 'Milestone',
    position: (r.position as number) ?? 0,
    description: (r.description as string) ?? null,
    condition: (r.condition as string) ?? null,
    createdAt: r.created_at as string,
  };
}

// ── Store ─────────────────────────────────────────────────────

export const useRoadmapStore = create<RoadmapState>()((set, get) => ({
  documents: [],
  nodes: [],
  edges: [],
  swimlanes: [],
  milestones: [],
  isLoading: false,
  loadedRoadmapId: null,
  error: null,

  fetchDocuments: async () => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return;

    set({ isLoading: true, error: null });
    const { data, error } = await supabase
      .from('roadmap_documents')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('[RoadmapStore] fetchDocuments error:', error);
      set({ isLoading: false, error: error.message });
      return;
    }
    set({
      documents: (data ?? []).map((r) => rowToDoc(r as Record<string, unknown>)),
      isLoading: false,
    });
  },

  fetchRoadmap: async (roadmapId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    set({ isLoading: true, error: null });

    const [nodesRes, edgesRes, swimlanesRes, milestonesRes] = await Promise.all([
      supabase.from('roadmap_nodes').select('*').eq('roadmap_id', roadmapId),
      supabase.from('roadmap_edges').select('*').eq('roadmap_id', roadmapId),
      supabase.from('roadmap_swimlanes').select('*').eq('roadmap_id', roadmapId).order('position'),
      supabase.from('roadmap_milestone_columns').select('*').eq('roadmap_id', roadmapId).order('position'),
    ]);

    set({
      nodes: (nodesRes.data ?? []).map((r) => rowToNode(r as Record<string, unknown>)),
      edges: (edgesRes.data ?? []).map((r) => rowToEdge(r as Record<string, unknown>)),
      swimlanes: (swimlanesRes.data ?? []).map((r) => rowToSwimlane(r as Record<string, unknown>)),
      milestones: (milestonesRes.data ?? []).map((r) => rowToMilestone(r as Record<string, unknown>)),
      loadedRoadmapId: roadmapId,
      isLoading: false,
    });
  },

  createRoadmap: async (input) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { roadmap: null, error: 'No Supabase client or workspace' };

    const { data, error } = await supabase
      .from('roadmap_documents')
      .insert({
        id: generateUUID(),
        workspace_id: workspaceId,
        title: input.title || 'Untitled Roadmap',
        objective: input.objective ?? '',
        future_point: input.futurePoint ?? '',
      })
      .select('*')
      .single();

    if (error) {
      console.error('[RoadmapStore] createRoadmap error:', error);
      return { roadmap: null, error: error.message };
    }

    const roadmap = rowToDoc(data as Record<string, unknown>);
    set((state) => ({ documents: [roadmap, ...state.documents] }));
    return { roadmap };
  },

  updateRoadmap: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const dbUpdates: Record<string, unknown> = {};
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.objective !== undefined) dbUpdates.objective = updates.objective;
    if (updates.futurePoint !== undefined) dbUpdates.future_point = updates.futurePoint;
    if (updates.definedSuccess !== undefined) dbUpdates.defined_success = updates.definedSuccess;
    if (updates.viewMode !== undefined) dbUpdates.view_mode = updates.viewMode;
    if (updates.cycleId !== undefined) dbUpdates.cycle_id = updates.cycleId;

    set((state) => ({
      documents: state.documents.map((d) => (d.id === id ? { ...d, ...updates } : d)),
    }));

    const { error } = await supabase.from('roadmap_documents').update(dbUpdates).eq('id', id);
    if (error) {
      console.error('[RoadmapStore] updateRoadmap error:', error);
      await get().fetchDocuments();
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  deleteRoadmap: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const snapshot = {
      documents: get().documents,
      nodes: get().nodes,
      edges: get().edges,
      swimlanes: get().swimlanes,
      milestones: get().milestones,
      loadedRoadmapId: get().loadedRoadmapId,
    };

    set((state) => ({
      documents: state.documents.filter((d) => d.id !== id),
      nodes: state.nodes.filter((n) => n.roadmapId !== id),
      edges: state.edges.filter((e) => e.roadmapId !== id),
      swimlanes: state.swimlanes.filter((s) => s.roadmapId !== id),
      milestones: state.milestones.filter((m) => m.roadmapId !== id),
      loadedRoadmapId: state.loadedRoadmapId === id ? null : state.loadedRoadmapId,
    }));

    const { error } = await supabase.from('roadmap_documents').delete().eq('id', id);
    if (error) {
      console.error('[RoadmapStore] deleteRoadmap error:', error);
      set({
        documents: snapshot.documents,
        nodes: snapshot.nodes,
        edges: snapshot.edges,
        swimlanes: snapshot.swimlanes,
        milestones: snapshot.milestones,
        loadedRoadmapId: snapshot.loadedRoadmapId,
      });
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  createNode: async (roadmapId, input) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { node: null, error: 'No Supabase client or workspace' };

    const maxPos = get()
      .nodes.filter((n) => n.roadmapId === roadmapId)
      .reduce((max, n) => Math.max(max, n.position), -1);

    const { data, error } = await supabase
      .from('roadmap_nodes')
      .insert({
        id: generateUUID(),
        roadmap_id: roadmapId,
        workspace_id: workspaceId,
        text: input.text ?? '',
        tense: input.tense ?? 'future',
        is_goal: input.isGoal ?? false,
        position: input.position ?? maxPos + 1,
        // The cell a condition is added in (Grid view's +): without these the
        // node lands under "Unassigned" and the cell you tapped is forgotten.
        lane_id: input.laneId ?? null,
        milestone_id: input.milestoneId ?? null,
        status: input.status ?? 'unknown',
        cell_relationship: input.cellRelationship ?? 'parallel',
        cell_connector_caption: input.cellConnectorCaption ?? null,
        tags: input.tags ?? [],
        metadata: input.metadata ?? {},
      })
      .select('*')
      .single();

    if (error) {
      console.error('[RoadmapStore] createNode error:', error);
      return { node: null, error: error.message };
    }

    const node = rowToNode(data as Record<string, unknown>);
    set((state) => ({ nodes: [...state.nodes, node] }));
    return { node };
  },

  updateNode: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const dbUpdates: Record<string, unknown> = {};
    if (updates.text !== undefined) dbUpdates.text = updates.text;
    if (updates.tense !== undefined) dbUpdates.tense = updates.tense;
    if (updates.isGoal !== undefined) dbUpdates.is_goal = updates.isGoal;
    if (updates.laneId !== undefined) dbUpdates.lane_id = updates.laneId;
    if (updates.milestoneId !== undefined) dbUpdates.milestone_id = updates.milestoneId;
    if (updates.position !== undefined) dbUpdates.position = updates.position;
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.cellRelationship !== undefined) dbUpdates.cell_relationship = updates.cellRelationship;
    if (updates.cellConnectorCaption !== undefined)
      dbUpdates.cell_connector_caption = updates.cellConnectorCaption;
    if (updates.tags !== undefined) dbUpdates.tags = updates.tags;
    if (updates.itemId !== undefined) dbUpdates.item_id = updates.itemId;
    if (updates.metadata !== undefined) dbUpdates.metadata = updates.metadata;

    // §2.1 — DB write first; local state updated only on success
    const { error } = await supabase.from('roadmap_nodes').update(dbUpdates).eq('id', id);
    if (error) {
      console.error('[RoadmapStore] updateNode error:', error);
      return { success: false, error: error.message };
    }

    set((state) => ({
      nodes: state.nodes.map((n) => (n.id === id ? { ...n, ...updates } : n)),
    }));
    return { success: true };
  },

  deleteNode: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const prevNodes = get().nodes;
    const prevEdges = get().edges;

    set((state) => ({
      nodes: state.nodes.filter((n) => n.id !== id),
      edges: state.edges.filter((e) => e.fromNodeId !== id && e.toNodeId !== id),
    }));

    const { error } = await supabase.from('roadmap_nodes').delete().eq('id', id);
    if (error) {
      console.error('[RoadmapStore] deleteNode error:', error);
      set({ nodes: prevNodes, edges: prevEdges });
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  createEdge: async (roadmapId, fromNodeId, toNodeId, note) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { edge: null, error: 'No Supabase client or workspace' };

    const tempId = generateUUID();
    const tempEdge: RoadmapEdge = {
      id: tempId,
      roadmapId,
      workspaceId,
      fromNodeId,
      toNodeId,
      note: note ?? null,
      createdAt: new Date().toISOString(),
    };
    set((state) => ({ edges: [...state.edges, tempEdge] }));

    const { data, error } = await supabase.rpc('create_roadmap_edge', {
      p_roadmap_id: roadmapId,
      p_workspace_id: workspaceId,
      p_from_node_id: fromNodeId,
      p_to_node_id: toNodeId,
      p_note: note ?? null,
    });

    const result = data as { id?: string; error?: string } | null;

    if (error || result?.error) {
      set((state) => ({ edges: state.edges.filter((e) => e.id !== tempId) }));
      const errorCode = result?.error as CreateEdgeError | undefined;
      const message = result?.error
        ? String(result.error)
        : error?.message ?? 'Failed to create edge';
      console.error('[RoadmapStore] createEdge error:', message);
      return { edge: null, error: message, errorCode: errorCode ?? 'network' };
    }

    const realEdge: RoadmapEdge = { ...tempEdge, id: result!.id! };
    set((state) => ({
      edges: state.edges.map((e) => (e.id === tempId ? realEdge : e)),
    }));
    return { edge: realEdge };
  },

  deleteEdge: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const prev = get().edges;
    set((state) => ({ edges: state.edges.filter((e) => e.id !== id) }));

    const { error } = await supabase.from('roadmap_edges').delete().eq('id', id);
    if (error) {
      console.error('[RoadmapStore] deleteEdge error:', error);
      set({ edges: prev });
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  createSwimlane: async (roadmapId, name) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { swimlane: null, error: 'No Supabase client or workspace' };

    const maxPos = get()
      .swimlanes.filter((s) => s.roadmapId === roadmapId)
      .reduce((max, s) => Math.max(max, s.position), -1);

    const { data, error } = await supabase
      .from('roadmap_swimlanes')
      .insert({ id: generateUUID(), roadmap_id: roadmapId, workspace_id: workspaceId, name, position: maxPos + 1 })
      .select('*')
      .single();

    if (error) return { swimlane: null, error: error.message };
    const swimlane = rowToSwimlane(data as Record<string, unknown>);
    set((state) => ({ swimlanes: [...state.swimlanes, swimlane] }));
    return { swimlane };
  },

  updateSwimlane: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };
    set((state) => ({ swimlanes: state.swimlanes.map((s) => (s.id === id ? { ...s, ...updates } : s)) }));
    const dbUpdates: Record<string, unknown> = {};
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.position !== undefined) dbUpdates.position = updates.position;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    const { error } = await supabase.from('roadmap_swimlanes').update(dbUpdates).eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  deleteSwimlane: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };
    const prev = get().swimlanes;
    set((state) => ({ swimlanes: state.swimlanes.filter((s) => s.id !== id) }));
    const { error } = await supabase.from('roadmap_swimlanes').delete().eq('id', id);
    if (error) { set({ swimlanes: prev }); return { success: false, error: error.message }; }
    return { success: true };
  },

  createMilestone: async (roadmapId, name) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { milestone: null, error: 'No Supabase client or workspace' };

    const maxPos = get()
      .milestones.filter((m) => m.roadmapId === roadmapId)
      .reduce((max, m) => Math.max(max, m.position), -1);

    const { data, error } = await supabase
      .from('roadmap_milestone_columns')
      .insert({ id: generateUUID(), roadmap_id: roadmapId, workspace_id: workspaceId, name, position: maxPos + 1 })
      .select('*')
      .single();

    if (error) return { milestone: null, error: error.message };
    const milestone = rowToMilestone(data as Record<string, unknown>);
    set((state) => ({ milestones: [...state.milestones, milestone] }));
    return { milestone };
  },

  updateMilestone: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const dbUpdates: Record<string, unknown> = {};
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.position !== undefined) dbUpdates.position = updates.position;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.condition !== undefined) dbUpdates.condition = updates.condition;

    set((state) => ({ milestones: state.milestones.map((m) => (m.id === id ? { ...m, ...updates } : m)) }));
    const { error } = await supabase.from('roadmap_milestone_columns').update(dbUpdates).eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  deleteMilestone: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };
    const prev = get().milestones;
    set((state) => ({ milestones: state.milestones.filter((m) => m.id !== id) }));
    const { error } = await supabase.from('roadmap_milestone_columns').delete().eq('id', id);
    if (error) { set({ milestones: prev }); return { success: false, error: error.message }; }
    return { success: true };
  },

  getDocumentById: (id) => get().documents.find((d) => d.id === id),
  getNodesForRoadmap: (roadmapId) => get().nodes.filter((n) => n.roadmapId === roadmapId),
  getEdgesForRoadmap: (roadmapId) => get().edges.filter((e) => e.roadmapId === roadmapId),
  getSwimlanesForRoadmap: (roadmapId) =>
    get().swimlanes.filter((s) => s.roadmapId === roadmapId).sort((a, b) => a.position - b.position),
  getMilestonesForRoadmap: (roadmapId) =>
    get().milestones.filter((m) => m.roadmapId === roadmapId).sort((a, b) => a.position - b.position),
}));
